/**
 * Calcul de layout du Gantt : ordre des lignes (hiérarchie phases/tâches), positions
 * verticales, fenêtre de virtualisation et détection de chevauchement entre tâches sœurs.
 *
 * Fonctions pures : aucun accès DOM.
 */

export const ROW_HEIGHT = 32;

/** Au-delà de ce nombre de lignes visibles, la virtualisation verticale s'active. */
export const VIRTUALIZE_THRESHOLD = 200;

/**
 * Construit la liste ordonnée des lignes (une par tâche), profondeur incluse, en respectant
 * la hiérarchie `parentId`. Une ligne dont un ancêtre est replié (`collapsedIds`) est marquée
 * `visible: false` mais reste dans le tableau (index stable pour les tests/consommateurs).
 */
export function buildRows(tasks, collapsedIds = []) {
  const collapsed = new Set(collapsedIds);
  const byParent = new Map();
  for (const task of tasks) {
    const key = task.parentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(task);
  }

  const rows = [];
  // `parentId` étant unique, un nœud n'apparaît aujourd'hui que sous une seule clé de `byParent`
  // et la récursion termine d'elle-même. Cette garde rend l'invariant explicite au lieu de le
  // laisser dépendre de cette structure : une tâche n'est rendue qu'une fois, et une hiérarchie
  // cyclique restée en base est écartée sans boucler.
  const visited = new Set();

  function walk(parentId, depth, hiddenByAncestor) {
    const children = byParent.get(parentId) ?? [];
    for (const task of children) {
      if (visited.has(task.id)) continue;
      visited.add(task.id);
      const hasChildren = (byParent.get(task.id) ?? []).length > 0;
      rows.push({ task, depth, hasChildren, visible: !hiddenByAncestor });
      const childHidden = hiddenByAncestor || collapsed.has(task.id);
      walk(task.id, depth + 1, childHidden);
    }
  }

  walk(null, 0, false);
  return rows;
}

/** Associe à chaque tâche visible sa position verticale (px), en ignorant les lignes repliées. */
export function assignRowPositions(rows, rowHeight = ROW_HEIGHT) {
  const positions = new Map();
  let y = 0;
  for (const row of rows) {
    if (!row.visible) continue;
    positions.set(row.task.id, y);
    y += rowHeight;
  }
  return positions;
}

/**
 * Fenêtre [start, end) des lignes visibles à peindre. En dessous de `VIRTUALIZE_THRESHOLD`
 * lignes, tout est rendu (pas de fenêtrage) — le coût de la virtualisation dépasserait le gain.
 */
export function computeVirtualWindow({
  visibleCount,
  rowHeight = ROW_HEIGHT,
  scrollTop = 0,
  viewportHeight = 0,
  overscan = 5,
}) {
  if (visibleCount <= VIRTUALIZE_THRESHOLD) {
    return { start: 0, end: visibleCount };
  }
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const end = Math.min(
    visibleCount,
    Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan,
  );
  return { start, end };
}

/**
 * Détecte les paires de tâches sœurs (même `parentId`) dont les intervalles [startDate, dueDate]
 * se chevauchent. Ignore les tâches sans dates (jalons purs gérés ailleurs si besoin).
 */
export function detectPhaseOverlaps(tasks) {
  const overlaps = [];
  const byParent = new Map();
  for (const task of tasks) {
    if (!task.startDate || !task.dueDate) continue;
    const key = task.parentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(task);
  }

  for (const siblings of byParent.values()) {
    for (let i = 0; i < siblings.length; i += 1) {
      for (let j = i + 1; j < siblings.length; j += 1) {
        const a = siblings[i];
        const b = siblings[j];
        const overlap = a.startDate <= b.dueDate && b.startDate <= a.dueDate;
        if (overlap) overlaps.push([a.id, b.id]);
      }
    }
  }
  return overlaps;
}
